import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  useGetCategories,
  useGetExercises,
  useGetObjectiveAttributes,
  useGetThemes,
  useGetTherapyAttributes,
} from '@/api/glow-up/glow-up.hooks';

import { AddCategory } from './category/AddCategory';
import { AddExercise } from './exercise/AddExercise';
import { AddObjectiveAttribute } from './objective-attribute/AddObjectiveAttribute';
import { AddTheme } from './theme/AddTheme';
import { AddTherapyAttribute } from './therapy-attribute/AddTherapyAttribute';
import { CategoryList } from './category/CategoryList';
import { ErrorScreen } from '@/components/ui/error-screen';
import { ExerciseList } from './exercise/ExerciseList';
import { GlowUpProductManagement } from '@/components/glow-up-product/GlowUpProductManagement';
import { LoadingScreen } from '@/components/ui/loading-screen';
import { ObjectiveAttributeList } from './objective-attribute/ObjectiveAttributeList';
import { ThemeList } from './theme/ThemeList';
import { TherapyAttributeList } from './therapy-attribute/TherapyAttributeList';
import { useState } from 'react';

export const GlowUpManagement = () => {
  const [activeTab, setActiveTab] = useState('themes');

  const handleTabChange = (value: string) => {
    if (value !== activeTab) {
      setActiveTab(value);
    }
  };

  // Load all categories (unpaginated) for the product-management category selector
  const { categories: allCategories, refetch: refetchCategories } =
    useGetCategories({ skip: 0, take: 1000 });

  // Refetch callbacks for Add/Edit/Delete buttons in theme and exercise tabs
  const { refetch: refetchThemes } = useGetThemes({
    skip: 0,
    take: 1000,
  });
  const { refetch: refetchExercises } = useGetExercises({
    skip: 0,
    take: 1000,
  });

  const {
    objectiveAttributes,
    isLoading: objectiveAttributesLoading,
    isError: objectiveAttributesError,
    refetch: refetchObjectiveAttributes,
  } = useGetObjectiveAttributes();
  const {
    therapyAttributes,
    isLoading: therapyAttributesLoading,
    isError: therapyAttributesError,
    refetch: refetchTherapyAttributes,
  } = useGetTherapyAttributes();

  if (objectiveAttributesLoading || therapyAttributesLoading) {
    return <LoadingScreen />;
  }

  if (objectiveAttributesError || therapyAttributesError) {
    return <ErrorScreen />;
  }

  return (
    <div className="container mx-auto p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold">Gestion Glow Up</h1>
      </div>

      <Tabs
        value={activeTab}
        className="w-full"
        onValueChange={handleTabChange}
      >
        <TabsList className="grid w-full grid-cols-6">
          <TabsTrigger value="themes">Thèmes</TabsTrigger>
          <TabsTrigger value="categories">Catégories</TabsTrigger>
          <TabsTrigger value="exercises">Exercices</TabsTrigger>
          <TabsTrigger value="objectiveAttributes">
            Attributs objectifs
          </TabsTrigger>
          <TabsTrigger value="therapyAttributes">
            Attributs thérapie
          </TabsTrigger>
          <TabsTrigger value="products">Produits</TabsTrigger>
        </TabsList>

        <TabsContent value="themes" className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-semibold">Thèmes</h2>
            <AddTheme refetch={refetchThemes} />
          </div>
          <ThemeList refetch={refetchThemes} />
        </TabsContent>

        <TabsContent value="categories" className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-semibold">Catégories</h2>
            <AddCategory refetch={refetchCategories} />
          </div>
          <CategoryList refetch={refetchCategories} />
        </TabsContent>

        <TabsContent value="exercises" className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-semibold">Exercices</h2>
            <AddExercise refetch={refetchExercises} />
          </div>
          <ExerciseList refetch={refetchExercises} />
        </TabsContent>

        <TabsContent value="objectiveAttributes" className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-semibold">
              Attributs d&apos;objectif
            </h2>
            <AddObjectiveAttribute refetch={refetchObjectiveAttributes} />
          </div>
          <ObjectiveAttributeList
            attributes={objectiveAttributes}
            refetch={refetchObjectiveAttributes}
          />
        </TabsContent>

        <TabsContent value="therapyAttributes" className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-semibold">Attributs de thérapie</h2>
            <AddTherapyAttribute refetch={refetchTherapyAttributes} />
          </div>
          <TherapyAttributeList
            attributes={therapyAttributes}
            refetch={refetchTherapyAttributes}
          />
        </TabsContent>

        <TabsContent value="products" className="space-y-4">
          <GlowUpProductManagement categories={allCategories ?? []} />
        </TabsContent>
      </Tabs>
    </div>
  );
};
